import React, { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { GetCastLibrary, GetCastMemberUrl } from '../../Client/ManagementServiceClient';

/**
 * Choose a face from the library instead of drawing a new one.
 *
 * A portrait is the one part of a run that cannot be reproduced — the same
 * prompt never draws the same face twice — so a character who recurs across
 * projects has to be picked, not regenerated. Redrawing them is how a series
 * ends up with a different-looking lead in every episode.
 *
 * The library is per USER, not per project, so what is offered here is every
 * face saved from anywhere.
 */
function CastPicker({ characterName, onPick, onClose, onError }) {
    const [members, setMembers] = useState([]);
    const [urls, setUrls] = useState({});
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await GetCastLibrary(idToken);
            if (status !== 200) throw new Error(myJson?.detail || 'Could not load your cast library');

            const list = myJson?.CastMembers || [];
            setMembers(list);

            // Portraits sit behind short-lived signed urls, fetched per member
            // rather than embedded in the list — same as the library view.
            const resolved = await Promise.all(list.map(async (member) => {
                const result = await GetCastMemberUrl(idToken, member.CastId);
                return [member.CastId, result.status === 200 ? result.myJson?.Url : ''];
            }));
            setUrls(Object.fromEntries(resolved));
        } catch (err) {
            onError?.(err.message);
        } finally {
            setLoading(false);
        }
    }, [onError]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="gen-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="gen-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Choose a face for ${characterName}`}
            >
                <div className="gen-modal-head">
                    <strong>Cast {characterName} from your library</strong>
                    <button type="button" className="gen-link" onClick={onClose}>Close</button>
                </div>

                {loading && <p className="gen-muted">Loading your cast…</p>}

                {!loading && members.length === 0 && (
                    <p className="gen-muted">
                        Nothing saved yet. Use <em>Save to library</em> on a character you like,
                        and they will be available in every project after that.
                    </p>
                )}

                {!loading && members.length > 0 && (
                    <div className="gen-cast-picker-grid">
                        {members.map((member) => (
                            <button
                                type="button"
                                key={member.CastId}
                                className="gen-cast-picker-item"
                                onClick={() => onPick(member)}
                                title={member.Description || member.Name}
                            >
                                {urls[member.CastId] ? (
                                    <img src={urls[member.CastId]} alt={member.Name} />
                                ) : (
                                    <div className="gen-portrait gen-portrait-empty">no portrait</div>
                                )}
                                <span>{member.Name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export { CastPicker };
